import React, { useEffect, useState } from 'react';
import { Modal, Tooltip } from '@mui/material';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import {
  getTicketsByClassTime,
  attendMembersToClassroom,
  attendMembersToClassroomMiss,
  attendMembersToClassroomDelete,
} from '../../../api/ticket';
import { errorMessages } from '../../../utils/methods';
import useUserAuth from '../../../utils/useUserAuth';
import { EVENT_TYPE_META } from './PrivateLessonStyled';
import { CancelConfirmModal, AttendanceConfirmModal } from './PrivateLessonModals';
import {
  AttendanceModalBox,
  AttendanceModalHeader,
  AttendanceModalTitle,
  AttendanceModalCloseIcon,
  AttendanceModalBody,
  AttendanceTypeBadge,
  AttendanceInfoBlock,
  AttendanceInfoTitleRow,
  AttendanceInfoDate,
  AttendanceInfoTrainer,
  MemberCard,
  MemberCardRow,
  MemberCardName,
  MemberCardLink,
  MEMBER_CARD_LINK_CLASS,
  MemberCardNameGroup,
  MemberCardPhone,
  MemberCardTicket,
  MemberCardStatusBadge,
  SessionStatBlock,
  SessionStatLabel,
  SessionStatValue,
  SessionProgressTrack,
  SessionProgressFill,
  AttendanceDuetNotice,
  AttendanceModalFooter,
  AttendanceActionGroup,
  AttendanceCancelButton,
  AttendanceAbsentButton,
  AttendanceAttendButton,
} from './PrivateLessonAttendanceModalStyled';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

// 참석 상태 배지 — 색+아이콘+텍스트 병행 (색만으로 상태를 전달하지 않음)
const STATUS_META = {
  ATTENDED: { label: '출석 완료', color: '#0F7F85', Icon: CheckCircleRoundedIcon },
  ABSENT: { label: '결석 처리', color: '#EE6A5B', Icon: CancelRoundedIcon },
  RESERVED: { label: '예약 완료', color: '#007FFF', Icon: EventAvailableRoundedIcon },
};

/**
 * 개인레슨 전용 참석관리 모달 (리뉴얼 디자인)
 * - 체크박스 없이 예약/출석/결석 회원 전원을 일괄 처리 (1:1 개인레슨 또는 듀엣 2인)
 * - props: { open, onClose, lesson, onEventDeleted } — lesson은 PrivateLessonList의 modalData
 */
const PrivateLessonAttendanceModal = ({ open, onClose, lesson, onEventDeleted }) => {
  const [users, setUsers] = useState([]);
  // 개인·듀엣은 예약을 취소하면 수업까지 삭제되므로 오클릭 방지를 위해 한 번 확인받는다.
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  // 출결 확정도 수강권을 차감하므로 확인받는다. null이면 닫힘, 'ATTENDED'/'ABSENT'면 해당 확인창.
  const [pendingAttendType, setPendingAttendType] = useState(null);
  // 출결 처리 API 응답 전 연타로 인한 중복 차감 방지
  const [isAttending, setIsAttending] = useState(false);
  const { user } = useSelector(state => state.user);
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isDuet = lesson?.courseClassType === 'DUET_LESSON';
  const isOt = lesson?.courseClassType === 'OT';
  const typeMeta = EVENT_TYPE_META[isDuet ? 'duet' : isOt ? 'ot' : 'pt'];

  // 권한 체크 (LessonUsers와 동일 기준)
  const canAttendance = useUserAuth('SCHEDULE_PRIVATE_ATTENDANCE');
  const canPastAttendance = useUserAuth('SCHEDULE_PRIVATE_PAST_ATTENDANCE');
  const canCancel = useUserAuth('SCHEDULE_PRIVATE_CANCEL');

  const classDate = lesson?.classDate || lesson?.registrationStartDate;
  const isPast = !!classDate && moment(classDate).isBefore(moment(), 'day');
  const canDoAttendance = isPast ? canPastAttendance : canAttendance;

  useEffect(() => {
    // 모달이 언마운트되지 않고 재사용되므로(다른 레슨을 이어서 열 수 있음), 레슨이 바뀔 때마다
    // 이전 레슨에서 남아있을 수 있는 처리중 상태를 초기화한다.
    setIsAttending(false);
    if (!open || !lesson?.courseClassTimeId) return;
    const fetchUsers = async () => {
      try {
        const data = await getTicketsByClassTime(lesson.courseClassTimeId, user.token);
        setUsers(data || []);
      } catch (error) {
        toast.error(errorMessages(error));
      }
    };
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lesson?.courseClassTimeId]);

  if (!lesson) return null;

  const targetUsers = users.filter(
    row => row.status === 'RESERVED' || row.status === 'ATTENDED' || row.status === 'ABSENT',
  );

  const formattedStart =
    classDate && lesson.startTime
      ? moment(`${classDate} ${lesson.startTime}`, 'YYYY-MM-DD HH:mm').format('YYYY년 M월 D일 (ddd) A h:mm')
      : '';
  const formattedEnd =
    classDate && lesson.endTime ? moment(`${classDate} ${lesson.endTime}`, 'YYYY-MM-DD HH:mm').format('A h:mm') : '';

  const handleClose = () => {
    setUsers([]);
    onClose();
  };

  const refreshUsers = async () => {
    const data = await getTicketsByClassTime(lesson.courseClassTimeId, user.token);
    setUsers(data || []);
  };

  const runAttendance = async attendType => {
    if (isAttending) return;
    setPendingAttendType(null);
    if (!canDoAttendance) return;
    if (targetUsers.length === 0) {
      toast.error('처리할 회원이 없습니다');
      return;
    }
    const body = { memberReservationIDs: targetUsers.map(row => row.memberReservationId) };
    setIsAttending(true);
    try {
      if (attendType) {
        await attendMembersToClassroom(lesson.courseClassTimeId, body, user.token);
        toast.success('출석 처리되었습니다');
      } else {
        await attendMembersToClassroomMiss(lesson.courseClassTimeId, body, user.token);
        toast.success('결석 처리되었습니다');
      }
      await refreshUsers();
      queryClient.invalidateQueries({ queryKey: ['groupLessons'] });
      queryClient.invalidateQueries({ queryKey: ['privateLessons'] });
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setIsAttending(false);
    }
  };

  const handleCancelReservation = async () => {
    setOpenCancelConfirm(false);
    if (!canCancel) return;
    if (targetUsers.length === 0) {
      toast.error('처리할 회원이 없습니다');
      return;
    }
    const body = { memberReservationIDs: targetUsers.map(row => row.memberReservationId) };
    try {
      await attendMembersToClassroomDelete(lesson.courseClassTimeId, body, user.token);
      toast.success('예약 취소를 완료하였습니다');
      queryClient.invalidateQueries({ queryKey: ['groupLessons'] });
      queryClient.invalidateQueries({ queryKey: ['privateLessons'] });
      handleClose();
      onEventDeleted?.(lesson.courseClassTimeId);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <AttendanceModalBox>
        <AttendanceModalHeader>
          <AttendanceModalTitle>참석 관리</AttendanceModalTitle>
          <AttendanceModalCloseIcon onClick={handleClose} />
        </AttendanceModalHeader>

        <AttendanceModalBody>
          <AttendanceInfoBlock>
            <AttendanceInfoTitleRow>
              <AttendanceInfoDate>
                {formattedStart} ~ {formattedEnd}
              </AttendanceInfoDate>
              <AttendanceTypeBadge badgeColor={typeMeta.color} badgeBg={typeMeta.badgeBg}>
                {typeMeta.label}
              </AttendanceTypeBadge>
            </AttendanceInfoTitleRow>
            {lesson.trainerName && <AttendanceInfoTrainer>담당 {lesson.trainerName}</AttendanceInfoTrainer>}
          </AttendanceInfoBlock>

          {targetUsers.map(row => {
            const statusMeta = STATUS_META[row.status] || { label: '-', color: '#666', Icon: null };
            const StatusIcon = statusMeta.Icon;
            const hasSession = row.remainingCount != null && row.totalCount != null;
            const remainRatio = hasSession && row.totalCount > 0 ? (row.remainingCount / row.totalCount) * 100 : 0;
            const sessionColor = hasSession && row.remainingCount <= 0 ? '#FF3B30' : '#0F7F85';
            return (
              <MemberCard key={row.memberId} statusKey={row.status}>
                <MemberCardRow>
                  <MemberCardNameGroup>
                    <MemberCardName>{row.memberName}</MemberCardName>
                    <MemberCardPhone>{row.phoneNumber}</MemberCardPhone>
                    <MemberCardLink
                      className={MEMBER_CARD_LINK_CLASS}
                      aria-label={`${row.memberName} 회원 정보로 이동`}
                      onClick={() => navigate(`/member/${row.memberId}`)}>
                      회원 정보
                      <ChevronRightRoundedIcon />
                    </MemberCardLink>
                  </MemberCardNameGroup>
                  <MemberCardStatusBadge badgeColor={statusMeta.color}>
                    {StatusIcon && <StatusIcon />}
                    {statusMeta.label}
                  </MemberCardStatusBadge>
                </MemberCardRow>
                <MemberCardRow>
                  <MemberCardTicket>{row.courseTicketName || '-'}</MemberCardTicket>
                  {/* 예약일 대신 수강권 만료일 — 날짜를 옮기거나 출결할 때 기간을 바로 본다 */}
                  {row.ticketExpireDate && <span>만료일 {String(row.ticketExpireDate).replaceAll('-', '.')}</span>}
                </MemberCardRow>
                <SessionStatBlock>
                  <SessionStatLabel>세션 잔여</SessionStatLabel>
                  <SessionStatValue valueColor={sessionColor}>
                    {hasSession ? (
                      <>
                        {row.remainingCount}
                        <small> / {row.totalCount}회</small>
                      </>
                    ) : (
                      '-'
                    )}
                  </SessionStatValue>
                  <SessionProgressTrack>
                    <SessionProgressFill fillPercent={remainRatio} fillColor={sessionColor} />
                  </SessionProgressTrack>
                </SessionStatBlock>
              </MemberCard>
            );
          })}

          {isOt && <AttendanceDuetNotice>* OT 는 전원 취소 시 수업도 함께 삭제됩니다. *</AttendanceDuetNotice>}
        </AttendanceModalBody>

        <AttendanceModalFooter>
          <Tooltip title={!canCancel ? '권한이 없습니다' : ''} arrow>
            <AttendanceCancelButton
              onClick={canCancel ? () => setOpenCancelConfirm(true) : undefined}
              disabledLook={!canCancel}>
              예약 및 대기 취소
            </AttendanceCancelButton>
          </Tooltip>
          <AttendanceActionGroup>
            <Tooltip title={!canDoAttendance ? '권한이 없습니다' : ''} arrow>
              <AttendanceAbsentButton
                onClick={canDoAttendance && !isAttending ? () => setPendingAttendType('ABSENT') : undefined}
                disabledLook={!canDoAttendance || isAttending}>
                결석 처리
              </AttendanceAbsentButton>
            </Tooltip>
            <Tooltip title={!canDoAttendance ? '권한이 없습니다' : ''} arrow>
              <AttendanceAttendButton
                onClick={canDoAttendance && !isAttending ? () => setPendingAttendType('ATTENDED') : undefined}
                disabledLook={!canDoAttendance || isAttending}>
                출석 처리
              </AttendanceAttendButton>
            </Tooltip>
          </AttendanceActionGroup>
        </AttendanceModalFooter>

        <CancelConfirmModal
          open={openCancelConfirm}
          onClose={() => setOpenCancelConfirm(false)}
          onConfirm={handleCancelReservation}
        />

        <AttendanceConfirmModal
          open={pendingAttendType !== null}
          attendType={pendingAttendType}
          onClose={() => setPendingAttendType(null)}
          onConfirm={() => runAttendance(pendingAttendType === 'ATTENDED')}
        />
      </AttendanceModalBox>
    </Modal>
  );
};

export default PrivateLessonAttendanceModal;
